import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@apollo/client/react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { AccessState } from "@/components/AccessState";

const FullPageSpinner = () => (
    <div className="bg-background text-muted-foreground flex min-h-screen items-center justify-center gap-3">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
    </div>
);

/**
 * Gate for every authenticated route.
 *
 * Being signed in with Auth0 is not the same as being able to use the app: the
 * account also has to exist here, and the API has to be reachable. Both used to fail
 * silently — the shell rendered with no name, no organizations and disabled
 * controls, which reads exactly like a permissions problem. Each state now says what
 * it is.
 *
 * Having an account here is not enough either: a plain USER is an app member, not an
 * operator, and is told so and offered the sign-out rather than being signed out and
 * dropped back on the login form.
 */
export function RequireAuth() {
    const { isAuthenticated, isLoading, logout } = useAuth0();
    const location = useLocation();
    const { data, loading, error, refetch } = useQuery(CurrentUserDocument, { skip: !isAuthenticated });

    const signOut = () => logout({ logoutParams: { returnTo: window.location.origin } });

    /*
     * The admin portal is for staff, org administrators and coaches. A plain USER belongs to the
     * member apps.
     *
     * They used to be logged straight out and dropped on the login form, which is the least
     * informative thing available: signing in appeared to do nothing at all. A pen tester spent a
     * day on it after landing in an environment where his account had been auto-provisioned at
     * the default role. Saying so, and offering the sign-out, is the same outcome explained —
     * and it removes the redirect that made a bare `<Navigate>` unusable here in the first place.
     */
    const isMemberOnly = data?.currentUser?.role === "USER";

    if (isLoading || (isAuthenticated && loading)) {
        return <FullPageSpinner />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
    }

    if (error) {
        /*
         * A rejected token is a different problem from an unreachable API, and the remedy
         * differs: sign in again versus wait and retry.
         *
         * The re-authentication codes are here too, because `apollo.tsx` now surfaces them rather
         * than redirecting a second time — and none of them contains the word "unauthorized", so
         * without this a denied sign-in reported "Can't reach the server", sending the reader
         * after an outage that was not happening.
         */
        const rejected =
            /unauthorized|unauthenticated|forbidden|401|403|login required|consent required|invalid.grant|refresh token/i.test(
                error.message,
            );
        return rejected ? (
            <AccessState kind="no-access" detail={error.message} onSignOut={signOut} />
        ) : (
            <AccessState kind="offline" detail={error.message} onRetry={() => void refetch()} />
        );
    }

    // Authenticated at the identity provider, but no account here.
    if (!data?.currentUser) {
        return <AccessState kind="no-access" onSignOut={signOut} />;
    }

    if (isMemberOnly) {
        return <AccessState kind="no-access" onSignOut={signOut} />;
    }

    return <Outlet />;
}
